/**
 * @param {string} status work status value
 * @return {object} badge config with label and className
 */
export const getWorkStatusBadge = (
  status: string,
): { label: string; className: string } => {
  switch (status) {
    case "working":
      return { label: "稼働中", className: BADGE.VIOLET_400 };
    case "ending_soon":
      return { label: "もうすぐ空き", className: BADGE.VIOLET_200 };
    case "available":
    default:
      return { label: "空き", className: BADGE.GRAY };
  }
};

/**
 * @param {string} affiliation affiliation value
 * @return {string} display label
 */
export const getAffiliationLabel = (affiliation: string): string => {
  switch (affiliation) {
    case "proprietary":
      return "自社";
    case "partner":
      return "パートナー";
    default:
      return affiliation;
  }
};

/**
 * @param {string} type employment type value
 * @return {string} display label
 */
export const getEmploymentTypeLabel = (type: string): string => {
  switch (type) {
    case "full_time":
      return "正社員";
    case "freelancer":
      return "フリーランス";
    case "contract":
      return "契約社員";
    default:
      return type;
  }
};

/**
 * @param {string} pref remote preference value
 * @return {string} display label
 */
export const getRemotePreferenceLabel = (
  pref: string | null | undefined,
): string => {
  switch (pref) {
    case "full_remote":
      return "フルリモート";
    case "hybrid":
      return "ハイブリッド";
    case "onsite_only":
      return "出社";
    default:
      return "-";
  }
};

/**
 * @param {string} stage pipeline stage value
 * @return {string} stage badge CSS class
 */
export const getStageBadgeClass = (stage: string): string => {
  const found = PIPELINE_STAGES.find(s => s.value === stage);
  return found?.badgeClass ?? "badge-ghost";
};

/**
 * 雇用形態（DB値）→ HRタイプ（プリセット/メール解析値）のマッピング
 * DB: full_time, freelancer, contract
 * HR: fullTimeEmployee, freelancer, contractEmployee
 */
export const EMPLOYMENT_TYPE_TO_HR_TYPE: Record<string, string> = {
  full_time: "fullTimeEmployee",
  freelancer: "freelancer",
  contract: "contractEmployee",
};

/**
 * HRタイプ（プリセット/メール解析値）→ 雇用形態（DB値）のマッピング
 */
export const HR_TYPE_TO_EMPLOYMENT_TYPE: Record<string, string> = {
  fullTimeEmployee: "full_time",
  contractEmployee: "contract",
  freelancer: "freelancer",
};

/**
 * @param {string} stage pipeline stage value
 * @return {string} display label
 */
export const getStageLabel = (stage: string): string => {
  switch (stage) {
    case "preparation":
      return "提案準備";
    case "proposed":
      return "提案済";
    case "doc_screening":
      return "書類通過";
    case "interview":
      return "面談中";
    case "offer":
      return "内定";
    case "won":
      return "成約";
    case "lost":
      return "見送り";
    default:
      return stage;
  }
};

// ================================================================
// バッジ色定数
// ================================================================

/** 紫系バッジ色（進捗の濃淡） */
export const BADGE = {
  VIOLET_100: "bg-violet-100 text-violet-600 border-violet-200",
  VIOLET_200: "bg-violet-200 text-violet-700 border-violet-300",
  VIOLET_300: "bg-violet-300 text-violet-800 border-violet-400",
  VIOLET_400: "bg-violet-400 text-white border-violet-500",
  VIOLET_600: "bg-violet-600 text-white border-violet-700",
  EMERALD: "bg-emerald-600 text-white border-emerald-700",
  GRAY: "bg-gray-300 text-gray-600 border-gray-400",
} as const;

// ================================================================
// 営業進捗ステージ
// ================================================================

export const PIPELINE_STAGES = [
  { value: "preparation", label: "提案準備", badgeClass: BADGE.VIOLET_100 },
  { value: "proposed", label: "提案済", badgeClass: BADGE.VIOLET_200 },
  { value: "doc_screening", label: "書類選考", badgeClass: BADGE.VIOLET_300 },
  { value: "interview", label: "面談", badgeClass: BADGE.VIOLET_400 },
  { value: "offer", label: "オファー", badgeClass: BADGE.VIOLET_600 },
  { value: "won", label: "成約", badgeClass: BADGE.EMERALD },
  { value: "lost", label: "失注", badgeClass: BADGE.GRAY },
] as const;

// ================================================================
// 案件ステータス
// ================================================================

export const FOCUS_MATTER_STATUSES = [
  { value: "open", label: "募集中", badgeClass: BADGE.VIOLET_200 },
  { value: "filling", label: "候補あり", badgeClass: BADGE.VIOLET_400 },
  { value: "filled", label: "充足", badgeClass: BADGE.EMERALD },
  { value: "closed", label: "クローズ", badgeClass: BADGE.GRAY },
] as const;

/**
 * 案件ステータスのラベルとバッジクラスを返す
 * @param {string} status ステータス値
 * @return {object} label, badgeClass
 */
export const getFocusMatterStatusBadge = (
  status: string,
): { label: string; badgeClass: string } => {
  const found = FOCUS_MATTER_STATUSES.find(s => s.value === status);
  return found ?? { label: status, badgeClass: "badge-ghost" };
};

// ================================================================
// 契約ステータス
// ================================================================

/**
 * 契約ステータスのラベルとバッジクラスを返す
 * @param {string} status 契約ステータス値
 * @return {object} label, badgeClass
 */
export const getContractStatusBadge = (
  status: string,
): { label: string; badgeClass: string } => {
  switch (status) {
    case "active":
      return { label: "稼働中", badgeClass: BADGE.VIOLET_400 };
    case "renewal_pending":
      return { label: "更新待ち", badgeClass: BADGE.VIOLET_200 };
    case "ended":
      return { label: "終了", badgeClass: BADGE.GRAY };
    case "renewed":
      return { label: "更新済", badgeClass: BADGE.EMERALD };
    default:
      return { label: status, badgeClass: BADGE.GRAY };
  }
};

// ================================================================
// 金額フォーマット
// ================================================================

/**
 * 円単位の金額を万円表示にフォーマットする
 * 端数がある場合は小数点1桁まで表示する
 * @param {number} yen 円単位の金額
 * @return {string} 万円表示の文字列（例: "68万円", "68.5万円"）
 */
export const formatYenToMan = (yen: number): string => {
  const man = yen / 10000;
  if (Number.isInteger(man)) {
    return `${man.toLocaleString()}万円`;
  }
  return `${man.toLocaleString(undefined, { maximumFractionDigits: 1 })}万円`;
};

/**
 * 円単位の金額を万の数値部分のみ返す（単位なし）
 * @param {number} yen 円単位の金額
 * @return {string} 万単位の数値文字列（例: "68", "68.5"）
 */
export const formatYenToManNum = (yen: number): string => {
  const man = yen / 10000;
  if (Number.isInteger(man)) {
    return man.toLocaleString();
  }
  return man.toLocaleString(undefined, { maximumFractionDigits: 1 });
};
